import { useRef, useCallback, useState } from 'react';

import type {
  GridCell,
  Item,
  EditableGridCell,
} from '@glideapps/glide-data-grid';

import { useMount } from '@simplywallst/ui-core';

import { ROW_BUFFER, COLUMN_IDS } from '../constants';

import type { Row, Lang } from '../types';

import {
  ContentCache,
  resetCell,
  createCell,
  recalculateTotalCostCell,
  updateRowCurrencyISO,
} from '../utils';

import { getMenuCellData, getMenuCellTemplate } from '../../CustomCell/Menu';
import { getDateCellData, getDateCellTemplate } from '../../CustomCell/Date';
import {
  isTickerCell,
  getTickerCellData,
  getTickerCellTemplate,
} from '../../CustomCell/Ticker';
import {
  isSharePriceCell,
  getSharePriceCellData,
  getSharePriceCellTemplate,
} from '../../CustomCell/SharePrice';
import {
  getTotalCostCellData,
  getTotalCostCellTemplate,
} from '../../CustomCell/TotalCost';
import {
  isSharesCell,
  getSharesCellData,
  getSharesCellTemplate,
} from '../../CustomCell/Shares';

import { isKindOfCustomCell } from '../../CustomCell/utils';

import { useTickerCellSearch } from '../hooks';
import { normarliseCacheData } from '../utils/normarliseCacheData';

type SelectedRowCellRange = {
  fromRow: number;
  toRow: number;
  fromColumn: number;
  toColumn: number;
};

export const useTableState = (data: Row[], lang: Lang) => {
  const { current: cache } = useRef<ContentCache>(new ContentCache());

  const dataRef = useRef(data);

  const [rowCount, setRowCount] = useState(dataRef.current.length + ROW_BUFFER);

  const { search } = useTickerCellSearch();

  const populateCache = useCallback(
    (data: Row[]) => {
      for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
        for (let colIndex = 0; colIndex < COLUMN_IDS.length; colIndex++) {
          const cell = createCell(COLUMN_IDS[colIndex], data[rowIndex], lang);
          cache.set(colIndex, rowIndex, cell);
        }
      }
    },
    [cache, lang]
  );

  // Populate cache with initial data
  useMount(() => {
    populateCache(dataRef.current);
  });

  const setRow = useCallback(
    (rowIndex: number, row: (GridCell | undefined)[]) => {
      if (row.length !== COLUMN_IDS.length) return;

      for (let i = 0; i < row.length; i++) {
        const cell = row[i];
        if (typeof cell === 'undefined') continue;
        cache.set(i, rowIndex, cell);
      }
    },
    [cache]
  );

  const getCellValue = useCallback(
    ([colIndex, rowIndex]: Item): GridCell => {
      let cell = cache.get(colIndex, rowIndex);

      // If a cell is defined, return it
      if (typeof cell !== 'undefined') return cell;

      // If requested cell is undefined, create a new cell
      const rowData = dataRef.current[rowIndex];

      cell = createCell(COLUMN_IDS[colIndex], rowData, lang);

      cache.set(colIndex, rowIndex, cell);

      return cell;
    },
    [dataRef, cache, lang]
  );

  const setCellValue = useCallback(
    (
      [colIndex, rowIndex]: Item,
      newValue: EditableGridCell,
      onSuccess?: () => void
    ) => {
      const current = cache.get(colIndex, rowIndex);

      const { data } = newValue;

      const columnId = COLUMN_IDS[colIndex];

      switch (columnId) {
        case 'ticker': {
          const tickerCellData = getTickerCellData(data);
          const { value, status } = tickerCellData;
          cache.set(colIndex, rowIndex, {
            ...current,
            ...getTickerCellTemplate({
              ...tickerCellData,
              status,
            }),
            lastUpdated: performance.now(),
          });
          // Update currencyISO for all cells in the row
          setRow(rowIndex, updateRowCurrencyISO(cache.getRow(rowIndex)));

          if (status === 'pending') {
            search(value).then((result) => {
              cache.set(colIndex, rowIndex, {
                ...current,
                ...result,
                lastUpdated: performance.now(),
              });
              // Update currencyISO for all cells in the row
              setRow(rowIndex, updateRowCurrencyISO(cache.getRow(rowIndex)));
              //
              onSuccess?.();
            });
          }

          break;
        }
        case 'date': {
          const dateCellData = getDateCellData(data);
          cache.set(colIndex, rowIndex, {
            ...current,
            ...getDateCellTemplate({ ...dateCellData }),
            lastUpdated: performance.now(),
          });
          break;
        }
        case 'type': {
          const menuCellData = getMenuCellData(data);
          cache.set(colIndex, rowIndex, {
            ...current,
            ...getMenuCellTemplate(menuCellData),
            lastUpdated: performance.now(),
          });
          break;
        }
        case 'price': {
          const sharePriceCellData = getSharePriceCellData(data);

          cache.set(colIndex, rowIndex, {
            ...current,
            ...getSharePriceCellTemplate(sharePriceCellData),
            lastUpdated: performance.now(),
          });
          // Recalculate total cost for the row
          setRow(rowIndex, recalculateTotalCostCell(cache.getRow(rowIndex)));
          break;
        }
        case 'shares': {
          const sharesCellData = getSharesCellData(data);

          cache.set(colIndex, rowIndex, {
            ...current,
            ...getSharesCellTemplate(sharesCellData),
            lastUpdated: performance.now(),
          });
          // Recalculate total cost for the row
          setRow(rowIndex, recalculateTotalCostCell(cache.getRow(rowIndex)));
          break;
        }
        case 'cost': {
          const { value } = getTotalCostCellData(data);
          cache.set(colIndex, rowIndex, {
            ...current,
            ...getTotalCostCellTemplate({ value }),
            lastUpdated: performance.now(),
          });
          break;
        }
      }

      onSuccess?.();
    },
    [cache, search, setRow]
  );

  const clearCellValue = useCallback(
    ([colIndex, rowIndex]: Item) => {
      const cell = getCellValue([colIndex, rowIndex]);

      if (isKindOfCustomCell(cell) && cell.readonly) return;

      cache.set(colIndex, rowIndex, resetCell(cell));

      if (isTickerCell(cell)) {
        // Clear currencyISO for all cells in the row when ticker is cleared
        setRow(rowIndex, updateRowCurrencyISO(cache.getRow(rowIndex)));
      }

      if (isSharePriceCell(cell) || isSharesCell(cell)) {
        // Recalculate total cost for the row
        setRow(rowIndex, recalculateTotalCostCell(cache.getRow(rowIndex)));
      }
    },
    [cache, getCellValue, setRow]
  );

  const clearBatchCellValues = useCallback(
    (range: SelectedRowCellRange) => {
      const { fromRow, toRow, fromColumn, toColumn } = range;
      for (let rowIndex = fromRow; rowIndex <= toRow; rowIndex++) {
        for (let colIndex = fromColumn; colIndex <= toColumn; colIndex++) {
          clearCellValue([colIndex, rowIndex]);
        }
      }
    },
    [clearCellValue]
  );

  const clearRow = useCallback(
    (rowIndex) => {
      for (let col = 0; col < COLUMN_IDS.length; col++) {
        const cell = getCellValue([col, rowIndex]);
        cache.set(col, rowIndex, resetCell(cell));
      }
    },
    [cache, getCellValue]
  );

  const duplicateRow = useCallback(
    (rowIndex) => {
      for (let row = rowCount; row > rowIndex; row--) {
        for (let col = 0; col < COLUMN_IDS.length; col++) {
          cache.set(col, row, getCellValue([col, row - 1]));
        }
      }
    },
    [cache, rowCount, getCellValue]
  );

  const insertRow = useCallback(
    (rowIndex) => {
      // Out of bounds
      if (rowIndex > rowCount || rowIndex < 0) return;

      duplicateRow(rowIndex);
      clearRow(rowIndex);

      setRowCount((rowCount) => rowCount + 1);
    },
    [rowCount, clearRow, duplicateRow]
  );

  const deleteRows = useCallback(
    (rows: number[]) => {
      if (rows.length === 0) return;
      cache.delete(rows);
      setRowCount((prev) => prev - rows.length);
    },
    [cache]
  );

  const getRows = useCallback(
    () => normarliseCacheData(cache.getCacheMap()),
    [cache]
  );

  const getColumn = useCallback(
    (colIndex: number) => cache.getCacheMap().get(colIndex),
    [cache]
  );

  const replaceAllRows = useCallback(
    (data: Row[]) => {
      cache.clear();
      dataRef.current = data;
      populateCache(dataRef.current);
      setRowCount(data.length + ROW_BUFFER);
    },
    [cache, populateCache]
  );

  return {
    getRows,
    getColumn,
    getCellValue,
    setCellValue,
    clearCellValue,
    clearBatchCellValues,
    deleteRows,
    insertRow,
    setRowCount,
    replaceAllRows,
    rowCount,
  };
};
